import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const buttonVariants = cva('btn text-center', {
  variants: {
    variant: {
      primary: 'btn-primary',
      secondary: 'btn-secondary',
      ghost: 'btn-ghost',
      'ghost-danger': 'btn-ghost-danger',
      danger: 'btn-danger',
      success: 'btn-success',
      // Deprecated shadcn aliases — prefer the canonical names above.
      // default -> primary, outline -> secondary, destructive -> danger.
      default: 'btn-primary',
      outline: 'btn-secondary',
      destructive: 'btn-danger',
    },
    size: {
      // Height stays padding-derived here. A responsive height in a variant
      // (`h-11 sm:h-8`) is NOT safely overridable by a call site: tailwind-merge
      // groups `h-*` and `sm:h-*` separately, so `className="h-10"` replaces
      // only the base rung and the variant's `sm:` rung still wins from 640px
      // up. That silently shrank every `<Button className="h-9|h-10|…">` in the
      // app above the breakpoint — ~50 call sites, including the Invoices
      // "Columns" button (h-10 → 32px) and the P&L controls row whose own fix
      // (#22867) pins `h-9`. Reverted rather than migrated: the override
      // pattern is too widespread to change safely in one pass.
      //
      // Rows that need a button to line up with a field should put it in a
      // field-height slot instead — see
      // `apps/ledger-web/src/routes/entities/tax/control_row_action.tsx`, which
      // centres the button without touching its size. `icon` keeps its explicit
      // square dimensions: it declares BOTH rungs and has no override sites.
      xs: 'btn-xs',
      sm: 'btn-sm',
      md: 'btn-md',
      lg: 'btn-lg',
      icon: 'h-11 w-11 justify-center p-0 sm:h-9 sm:w-9',
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'md',
  },
});

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** When true, renders the button in a loading/pending state and disables interaction. */
  loading?: boolean;
  /** When true, renders the single child element with button styling applied. */
  asChild?: boolean;
  /** Button visual variant */
  variant?:
    | 'primary'
    | 'secondary'
    | 'ghost'
    | 'ghost-danger'
    | 'danger'
    | 'success'
    | 'default'
    | 'outline'
    | 'destructive';
  /** Button size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'icon';
  /**
   * Makes the button a toggle: sets `aria-pressed` and, while `true`, the
   * pressed treatment (accent-soft fill + accent text, the same tokens as an
   * active `Tabs` pill). Leave `undefined` for an ordinary button.
   */
  pressed?: boolean;
}

/** Pressed toggle treatment — mirrors the active `.nv-tab-pill` tokens. */
const PRESSED_CLASSES =
  'border-[var(--accent-light)] bg-[var(--color-accent-soft)] text-[var(--accent)] font-semibold hover:bg-[var(--color-accent-soft)]';

function LoadingSpinner({
  size = 'sm',
}: { size?: 'sm' | 'md' } = {}): React.ReactElement {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'nv-spinner inline-block animate-spin',
        size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5',
      )}
    />
  );
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      disabled,
      asChild = false,
      pressed,
      children,
      ...props
    },
    ref,
  ) => {
    const buttonClasses = cn(
      buttonVariants({ variant, size }),
      pressed === true && PRESSED_CLASSES,
      className,
    );
    const pressed_props =
      pressed === undefined ? {} : { 'aria-pressed': pressed };

    if (asChild && React.Children.count(children) === 1) {
      // eslint-disable-next-line no-restricted-syntax -- React.Children.only is a legitimate React API
      const child = React.Children.only(children);
      if (React.isValidElement<{ className?: string }>(child)) {
        return React.cloneElement(child, {
          className: cn(child.props.className, buttonClasses),
          ...pressed_props,
          ...props,
        });
      }
    }

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...pressed_props}
        {...props}
      >
        {loading && <LoadingSpinner size="sm" />}
        {children}
      </button>
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants, LoadingSpinner };
export type { ButtonProps };
